import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type { GmailThreadDto } from '../types/quotation.types';

// CSC-294: per-session Gmail-thread cache so the Conversation tab feels instant when
// sales opens a quotation they hovered. Keys are gmailThreadId; cache entries expire
// after 5 minutes so a thread that gets a new reply is eventually refreshed without
// any explicit invalidation. In-flight dedupe prevents two near-simultaneous requests
// (e.g. hover prefetch + tab mount fetch) from issuing two upstream calls.
const TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  data: GmailThreadDto;
  fetchedAt: number;
}

class GmailThreadService {
  private cache = new Map<string, CacheEntry>();
  private inflight = new Map<string, Promise<GmailThreadDto>>();

  async get(threadId: string): Promise<GmailThreadDto> {
    const cached = this.cache.get(threadId);
    if (cached && Date.now() - cached.fetchedAt < TTL_MS) {
      return cached.data;
    }

    const existing = this.inflight.get(threadId);
    if (existing) return existing;

    const promise = this.fetch(threadId);
    this.inflight.set(threadId, promise);
    try {
      const data = await promise;
      this.cache.set(threadId, { data, fetchedAt: Date.now() });
      return data;
    } finally {
      this.inflight.delete(threadId);
    }
  }

  // Fire-and-forget warm-up triggered by master-list row hover. Errors are swallowed
  // because a failed prefetch is not a real-user-visible problem — the next call to
  // get() will retry and surface any error to the actual UI.
  prefetch(threadId: string): void {
    if (!threadId) return;
    const cached = this.cache.get(threadId);
    if (cached && Date.now() - cached.fetchedAt < TTL_MS) return;
    if (this.inflight.has(threadId)) return;
    this.get(threadId).catch(() => { /* swallow */ });
  }

  invalidate(threadId: string): void {
    this.cache.delete(threadId);
  }

  private async fetch(threadId: string): Promise<GmailThreadDto> {
    const { data } = await apiClient.get<ApiResponse<GmailThreadDto>>(
      `/email-automation/thread/${encodeURIComponent(threadId)}`,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load Gmail thread');
  }
}

export const gmailThreadService = new GmailThreadService();
